import { useMemo, type ReactNode } from "react";

import { ADS_MANAGER_SURFACE_CLASS } from "../../constants/layout";
import { COPY } from "../../copy";
import { getMonthCohortRows, monthLabelFromIndex } from "../../customersData";
import {
  getCumulativeSalesSeries,
  getMonthDetailKpis,
  getMonthDetailKpiTooltips,
  MONTH_DETAIL_KPI_TOOLTIPS,
} from "../../monthDetailData";
import {
  formatOverviewCurrency,
  formatOverviewRoas,
} from "../../p8OverviewData";
import { fillCopy } from "../../renderCopy";
import { AdsManagerContent } from "../AdsManagerContent";
import {
  useCardPaddingClass,
  useIsCompact,
  useStackGapClass,
} from "../CardDensity";
import { useForecastModal } from "../ForecastExplainer";
import { DottedLabel } from "../overview/DottedLabel";
import { HeaderTooltip } from "../overview/HeaderTooltip";

import { CohortConversionsTable } from "./CohortConversionsTable";
import { FirstYearSalesAndSpendChart } from "./FirstYearSalesAndSpendChart";

const V2_COPY = COPY.cohort.v2;
/** Shared KPI builder still titles this card "Ad sales"; cohort views relabel it. */
const IMMEDIATE_KPI_LOOKUP = "Ad sales";

type MonthDetailPageV2Props = {
  monthIndex: number;
  onBack: () => void;
  isV3?: boolean;
};

function BackChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M10 3L5 8L10 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * One cell of the 2x2 metrics grid. The footer slot takes nodes rather than
 * strings because the first-year metric puts an explainer link where the other
 * three put plain supporting figures.
 */
function MetricCell({
  title,
  tooltip,
  value,
  footer,
}: {
  title: string;
  tooltip: string;
  value: string;
  footer?: ReactNode;
}) {
  const isCompact = useIsCompact();

  return (
    <div className="text-sm leading-5">
      <HeaderTooltip tooltip={tooltip}>
        <DottedLabel>{title}</DottedLabel>
      </HeaderTooltip>
      <p className="mt-1 flex items-center gap-2 text-[30px] leading-[38px] font-normal text-[#333]">
        {value}
      </p>
      {footer ? (
        <div className={isCompact ? "mt-1 space-y-0.5" : "mt-2 space-y-1"}>
          {footer}
        </div>
      ) : null}
    </div>
  );
}

function FooterLine({ children }: { children: ReactNode }) {
  return <p className="text-xs leading-4 text-[#757575]">{children}</p>;
}

export function MonthDetailPageV2({
  monthIndex,
  onBack,
  isV3 = false,
}: MonthDetailPageV2Props) {
  const openForecastModal = useForecastModal();
  const cardPaddingClass = useCardPaddingClass();
  const stackGapClass = useStackGapClass();
  // Short form in the heading and tooltips so they read the same as the chart
  // axes beside them (`Jan 2026`, not "January 2026").
  const monthShort = monthLabelFromIndex(monthIndex);
  const tooltips = getMonthDetailKpiTooltips(monthIndex);
  const series = useMemo(
    () => getCumulativeSalesSeries(monthIndex),
    [monthIndex]
  );
  const cohortRows = useMemo(
    () => getMonthCohortRows(monthIndex),
    [monthIndex]
  );

  /**
   * Every headline figure is read off the same series the chart plots, so the
   * metrics can't drift from the lines beside them. The one exception is the
   * ad-type split under the immediate-sales card, which the series doesn't carry — it
   * comes from the shared KPI builder v1 uses, so both versions report the same
   * breakdown.
   */
  const summary = useMemo(() => {
    const immediate = series[0]?.firstOrderSales ?? 0;
    const realized = series.filter((point) => point.salesToDate != null);
    const latest = realized[realized.length - 1];
    const total = latest?.salesToDate ?? immediate;

    // The +12-month mark, which the series carries for every cohort. Past that
    // age it stops being a forecast: the first-year figure is simply what
    // landed, so `forecasted` is what decides the card's title and its link.
    const marker = series.find(
      (point) => point.projectedFirstYearSales != null
    )?.projectedFirstYearSales;
    const forecasted = marker != null && marker > total;

    return {
      immediate,
      firstYear: marker ?? total,
      // Taken from the point rather than divided out here, so the card can't
      // disagree with the Overview column that reports the same ratio.
      firstYearRoas:
        series.find((point) => point.projectedRoas != null)?.projectedRoas ??
        null,
      forecasted,
    };
  }, [series]);

  const monthKpis = useMemo(() => getMonthDetailKpis(monthIndex), [monthIndex]);
  const conversionsKpi = monthKpis.find((kpi) => kpi.title === "Conversions");
  const immediateFooters = useMemo(
    () =>
      monthKpis.find((kpi) => kpi.title === IMMEDIATE_KPI_LOOKUP)?.footers ??
      [],
    [monthKpis]
  );

  return (
    <div
      className={`min-h-0 flex-1 overflow-y-auto ${ADS_MANAGER_SURFACE_CLASS}`}
    >
      <AdsManagerContent className="pt-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-[#757575] hover:text-[#333]"
        >
          <BackChevronIcon />
          Overview
        </button>
        <h1 className="mt-3 text-[30px] leading-[38px] font-semibold text-[#333]">
          {fillCopy(isV3 ? V2_COPY.v3Heading : V2_COPY.heading, {
            month: monthShort,
          })}
        </h1>
        <p className="mt-2 text-sm leading-5 text-[#757575]">
          {V2_COPY.subhead}
        </p>
      </AdsManagerContent>
      <div className="mt-4 border-b border-[#dfe0e1]" />
      <AdsManagerContent className="pt-6 pb-32">
        <div className={`flex flex-col ${stackGapClass}`}>
          {/* Metrics and chart share a row: the four figures are the totals the
              chart's series add up to, so they're read together. They stack
              below 1100px, where two half-width cards stop fitting the numbers. */}
          <div className={`grid ${stackGapClass} min-[1100px]:grid-cols-2`}>
            <div
              className={`bg-fs-surface-primary rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
            >
              {/* The chart beside it is the taller card, so this one is stretched
                  to match and the four figures would otherwise sit in a block at
                  the top with the slack pooled underneath. `content-between`
                  spends that slack between the two rows instead, so the metrics
                  span the card the way the chart's plot does. */}
              <div
                className={`grid grid-cols-1 ${stackGapClass} min-[520px]:grid-cols-2 ${
                  isV3 ? "h-full content-between" : ""
                }`}
              >
                <MetricCell
                  title="Conversions"
                  tooltip={tooltips.conversions}
                  value={conversionsKpi?.value ?? "0"}
                  footer={
                    conversionsKpi?.footers.map((line) => (
                      <FooterLine key={line}>{line}</FooterLine>
                    )) ?? null
                  }
                />
                <MetricCell
                  title={isV3 ? "Immediate sales" : "Immediate ad sales"}
                  tooltip={tooltips.immediateAdSales}
                  value={formatOverviewCurrency(summary.immediate)}
                  footer={immediateFooters.map((line) => (
                    <FooterLine key={line}>{line}</FooterLine>
                  ))}
                />
                <MetricCell
                  title={
                    summary.forecasted
                      ? MONTH_DETAIL_KPI_TOOLTIPS.firstYearForecastTitle
                      : MONTH_DETAIL_KPI_TOOLTIPS.firstYearTitle
                  }
                  tooltip={
                    summary.forecasted
                      ? tooltips.firstYearAdSalesForecast
                      : tooltips.firstYearAdSales
                  }
                  value={formatOverviewCurrency(summary.firstYear)}
                  footer={
                    // Only while the figure is still moving. A matured cohort's
                    // first year is settled, so there is no forecast left to
                    // explain, and the link would raise a doubt about a final
                    // number rather than answering one.
                    summary.forecasted && openForecastModal ? (
                      <button
                        type="button"
                        className="text-xs leading-4 text-[#333] underline"
                        onClick={openForecastModal}
                      >
                        {COPY.shared.forecast.inlineLink}
                      </button>
                    ) : null
                  }
                />
                {/* Last, because it's the card to its left over the month's ad
                    spend. Em dash rather than 0.0x for a month with no spend:
                    there is no return to report, not a return of nothing.
                    The (forecasted) qualifier and the explainer link are the
                    same gate as the sales card: both figures are estimates
                    until the cohort is 12 months old. */}
                <MetricCell
                  title={
                    summary.forecasted
                      ? MONTH_DETAIL_KPI_TOOLTIPS.firstYearRoasForecastTitle
                      : MONTH_DETAIL_KPI_TOOLTIPS.firstYearRoasTitle
                  }
                  tooltip={fillCopy(
                    summary.forecasted
                      ? V2_COPY.kpi.firstYearRoas
                      : V2_COPY.kpi.firstYearRoasFinal,
                    { month: monthShort }
                  )}
                  value={
                    summary.firstYearRoas == null
                      ? "—"
                      : formatOverviewRoas(summary.firstYearRoas)
                  }
                  footer={
                    summary.forecasted && openForecastModal ? (
                      <button
                        type="button"
                        className="text-xs leading-4 text-[#333] underline"
                        onClick={openForecastModal}
                      >
                        {COPY.shared.forecast.inlineLink}
                      </button>
                    ) : null
                  }
                />
              </div>
            </div>

            <FirstYearSalesAndSpendChart data={series} isV3={isV3} />
          </div>

          <CohortConversionsTable rows={cohortRows} month={monthShort} />
        </div>
      </AdsManagerContent>
    </div>
  );
}
